/**
 * Financial feature event tracking
 */

import { analytics } from '../index';
import { anonymizeAmount } from '../privacy';

export const financialEvents = {
  // Budget Events
  budgets: {
    created: async (data: {
      budgetName: string;
      categoryCount: number;
      totalAmount: number;
      currency: string;
      isFirstBudget: boolean;
    }) => {
      await analytics.track('budget_created', {
        category: 'budgets',
        feature_area: 'budget_management',
        action_type: 'create',
        amount_range: anonymizeAmount(data.totalAmount),
        currency: data.currency,
        category_count: data.categoryCount,
        is_first_budget: data.isFirstBudget,
      });
    },

    updated: async (data: {
      updateType: 'amount' | 'category_added' | 'category_removed' | 'renamed';
      categoryCount?: number;
      changeAmount?: number;
    }) => {
      await analytics.track('budget_updated', {
        category: 'budgets',
        feature_area: 'budget_management',
        action_type: 'update',
        update_type: data.updateType,
        category_count: data.categoryCount,
        change_amount_range: data.changeAmount ? anonymizeAmount(data.changeAmount) : undefined,
      });
    },

    deleted: async (data: {
      hadTransactions: boolean;
      ageInDays: number;
    }) => {
      await analytics.track('budget_deleted', {
        category: 'budgets',
        feature_area: 'budget_management',
        action_type: 'delete',
        had_transactions: data.hadTransactions,
        age_in_days: data.ageInDays,
      });
    },

    overspendAlert: async (data: {
      categoryName: string;
      percentOver: number;
      notificationSent: boolean;
    }) => {
      await analytics.track('budget_overspend_alert', {
        category: 'budgets',
        feature_area: 'budget_alerts',
        action_type: 'alert',
        percent_over: Math.round(data.percentOver),
        notification_sent: data.notificationSent,
      });
    },
  },

  // Transaction Events
  transactions: {
    added: async (data: {
      amount: number;
      category: string;
      accountType: string;
      isRecurring: boolean;
      source: 'manual' | 'import' | 'bank_sync';
    }) => {
      await analytics.track('transaction_added', {
        category: 'transactions',
        feature_area: 'transaction_management',
        action_type: 'create',
        amount_range: anonymizeAmount(data.amount),
        transaction_category: data.category,
        account_type: data.accountType,
        is_recurring: data.isRecurring,
        source: data.source,
      });
    },

    edited: async (data: {
      fieldsChanged: string[];
      amountChanged: boolean;
      categoryChanged: boolean;
    }) => {
      await analytics.track('transaction_edited', {
        category: 'transactions',
        feature_area: 'transaction_management',
        action_type: 'edit',
        fields_changed: data.fieldsChanged.join(','),
        amount_changed: data.amountChanged,
        category_changed: data.categoryChanged,
      });
    },

    deleted: async (data: {
      count: number;
      isBulk: boolean;
    }) => {
      await analytics.track('transaction_deleted', {
        category: 'transactions',
        feature_area: 'transaction_management',
        action_type: 'delete',
        count: data.count,
        is_bulk: data.isBulk,
      });
    },

    categorized: async (data: {
      count: number;
      fromCategory: string;
      toCategory: string;
      method: 'manual' | 'rule' | 'ai_suggestion';
    }) => {
      await analytics.track('transaction_categorized', {
        category: 'transactions',
        feature_area: 'categorization',
        action_type: 'categorize',
        count: data.count,
        from_category: data.fromCategory,
        to_category: data.toCategory,
        method: data.method,
      });
    },

    imported: async (data: {
      count: number;
      source: 'csv' | 'bank_file' | 'manual';
      successRate: number;
    }) => {
      await analytics.track('transactions_imported', {
        category: 'transactions',
        feature_area: 'import',
        action_type: 'import',
        count: data.count,
        source: data.source,
        success_rate: Math.round(data.successRate * 100),
      });
    },

    reconciled: async (data: {
      count: number;
      accountType: string;
      discrepancyFound: boolean;
    }) => {
      await analytics.track('transactions_reconciled', {
        category: 'transactions',
        feature_area: 'reconciliation',
        action_type: 'reconcile',
        count: data.count,
        account_type: data.accountType,
        discrepancy_found: data.discrepancyFound,
      });
    },
  },

  // Account Events
  accounts: {
    added: async (data: {
      accountType: string;
      connectionMethod: 'manual' | 'plaid' | 'other';
      isFirstAccount: boolean;
      initialBalance?: number;
    }) => {
      await analytics.track('account_added', {
        category: 'accounts',
        feature_area: 'account_management',
        action_type: 'add',
        account_type: data.accountType,
        connection_method: data.connectionMethod,
        is_first_account: data.isFirstAccount,
        has_initial_balance: data.initialBalance !== undefined,
      });
    },

    connected: async (data: {
      provider: 'plaid' | 'other';
      accountCount: number;
      connectionTime: number; // in seconds
    }) => {
      await analytics.track('bank_account_connected', {
        category: 'accounts',
        feature_area: 'bank_connection',
        action_type: 'connect',
        provider: data.provider,
        account_count: data.accountCount,
        connection_time: data.connectionTime,
      });
    },

    synced: async (data: {
      accountCount: number;
      transactionCount: number;
      syncDuration: number; // in seconds
      hadErrors: boolean;
    }) => {
      await analytics.track('accounts_synced', {
        category: 'accounts',
        feature_area: 'sync',
        action_type: 'sync',
        account_count: data.accountCount,
        transaction_count: data.transactionCount,
        sync_duration: data.syncDuration,
        had_errors: data.hadErrors,
      });
    },

    disconnected: async (data: {
      reason: 'user_initiated' | 'error' | 'reauth_required';
      hadActiveSync: boolean;
      ageInDays: number;
    }) => {
      await analytics.track('account_disconnected', {
        category: 'accounts',
        feature_area: 'bank_connection',
        action_type: 'disconnect',
        reason: data.reason,
        had_active_sync: data.hadActiveSync,
        age_in_days: data.ageInDays,
      });
    },

    balanceUpdated: async (data: {
      updateMethod: 'manual' | 'sync';
      accountType: string;
    }) => {
      await analytics.track('account_balance_updated', {
        category: 'accounts',
        feature_area: 'balance_tracking',
        action_type: 'update',
        update_method: data.updateMethod,
        account_type: data.accountType,
      });
    },
  },

  // Goal Events
  goals: {
    created: async (data: {
      goalType: string;
      targetAmount: number;
      targetDate?: Date;
      linkedAccounts: number;
    }) => {
      await analytics.track('goal_created', {
        category: 'goals',
        feature_area: 'goal_tracking',
        action_type: 'create',
        goal_type: data.goalType,
        amount_range: anonymizeAmount(data.targetAmount),
        has_target_date: !!data.targetDate,
        linked_accounts: data.linkedAccounts,
      });
    },

    achieved: async (data: {
      goalType: string;
      daysToAchieve: number;
      percentOverTarget?: number;
    }) => {
      await analytics.track('goal_achieved', {
        category: 'goals',
        feature_area: 'goal_tracking',
        action_type: 'achieve',
        goal_type: data.goalType,
        days_to_achieve: data.daysToAchieve,
        percent_over_target: data.percentOverTarget,
      });
    },

    progressUpdated: async (data: {
      goalType: string;
      progressPercent: number;
      isAheadOfSchedule: boolean;
    }) => {
      await analytics.track('goal_progress_updated', {
        category: 'goals',
        feature_area: 'goal_tracking',
        action_type: 'progress',
        goal_type: data.goalType,
        progress_percent: Math.round(data.progressPercent),
        is_ahead_of_schedule: data.isAheadOfSchedule,
      });
    },
  },
};